import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Modal, Input, Select, Textarea, StatusBadge, EmptyState,
  SearchBar, Pagination, FilterBar, PageHeader, FilterSelect, ListSkeleton,
  IconButton, Chip, Field, StickyActions, Badge, formatINR, formatDate, cx,
  ListPage, ListSurface, stickyHeadCls, actionColCls, actionHeadCls,
  listTableWideCls, listCardsWideCls, thCls, tdCls, actionW,
  siteTimeline, relativeDate, dateWithRelative, activityTone,
} from "../components/UI";
import api from "../utils/api";
import toast from "react-hot-toast";
import ClientSelectPopup from "../components/ClientSelectPopup";
import {
  Plus, Pencil, Trash2, Eye, MapPin, Users, Truck, Telescope, Receipt,
  ArrowUpDown, ArrowUp, ArrowDown, SlidersHorizontal, ExternalLink, Navigation,
} from "lucide-react";

const LIMIT = 15;
const INIT = { name: "", code: "", address: "", city: "", state: "", country: "India", status: "active", clientId: "", description: "", startDate: "", endDate: "" };
const STATUSES = ["active", "on-hold", "completed", "inactive"];

const STATUS_RING = {
  active: "border-brand-300 text-brand-600 bg-brand-50",
  inactive: "border-ink-200 text-ink-500 bg-ink-100",
  completed: "border-emerald-300 text-emerald-600 bg-emerald-50",
  "on-hold": "border-amber-300 text-amber-600 bg-amber-50",
};

const BILL_TONE = {
  draft: "bg-ink-100 text-ink-500", sent: "bg-brand-50 text-brand-700",
  paid: "bg-emerald-50 text-emerald-700", overdue: "bg-red-50 text-red-700",
  cancelled: "bg-ink-100 text-ink-400 line-through",
};

/* ── list filters (everything except paging / sorting) ── */
const INIT_FILTERS = {
  search: "", status: "", clientId: "",
  city: "", state: "", country: "",
  crewId: "", vehicleId: "", instrumentId: "",
  hasLocation: "", hasClient: "", hasCrews: "", hasVehicles: "", hasInstruments: "", hasBills: "",
  startFrom: "", startTo: "", endFrom: "", endTo: "", activeOn: "",
};

const SORT_OPTIONS = [
  { value: "createdAt:desc", label: "Newest added" },
  { value: "createdAt:asc", label: "Oldest added" },
  { value: "updatedAt:desc", label: "Recently updated" },
  { value: "name:asc", label: "Name A → Z" },
  { value: "name:desc", label: "Name Z → A" },
  { value: "code:asc", label: "Code A → Z" },
  { value: "city:asc", label: "City A → Z" },
  { value: "status:asc", label: "Status A → Z" },
  { value: "startDate:desc", label: "Start date — newest" },
  { value: "startDate:asc", label: "Start date — oldest" },
  { value: "endDate:asc", label: "End date — soonest" },
  { value: "endDate:desc", label: "End date — latest" },
  { value: "crews:desc", label: "Most crew" },
  { value: "vehicles:desc", label: "Most vehicles" },
  { value: "instruments:desc", label: "Most instruments" },
  { value: "bills:desc", label: "Most bills" },
];

// Text columns read better ascending on first click; dates and counts descending.
const TEXT_SORT_FIELDS = ["name", "code", "city", "state", "country", "status"];
const DEFAULT_ORDER = (field) => (TEXT_SORT_FIELDS.includes(field) ? "asc" : "desc");

/* ═══ Assigned resources ═══ */
function AssignedResources({ site, compact = false }) {
  const groups = [
    { items: site.assignedCrews || [], icon: <Users size={12} />, label: "Crew", tone: "violet" },
    { items: site.assignedVehicles || [], icon: <Truck size={12} />, label: "Vehicles", tone: "sky" },
    { items: site.assignedInstruments || [], icon: <Telescope size={12} />, label: "Instruments", tone: "amber" },
  ];
  if (!groups.some(g => g.items.length)) {
    return <span className="text-ink-300 text-xs">None assigned</span>;
  }

  if (compact) {
    return (
      <div className="space-y-1.5">
        {groups.filter(g => g.items.length).map(g => (
          <div key={g.label} className="flex flex-wrap gap-1 items-center">
            <span className="text-ink-400 shrink-0">{g.icon}</span>
            {g.items.slice(0, 2).map(x => <Chip key={x._id || x} tone={g.tone}>{x.name || x}</Chip>)}
            {g.items.length > 2 && <Chip tone="slate">+{g.items.length - 2}</Chip>}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.filter(g => g.items.length).map(g => (
        <div key={g.label}>
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-ink-400 uppercase tracking-wider mb-2">
            {g.icon} {g.label}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {g.items.map(x => (
              <Chip key={x._id || x} tone={g.tone}>
                {x.name || x}
                {x.role && <span className="opacity-60"> · {x.role}</span>}
                {x.registrationNumber && <span className="opacity-60"> · {x.registrationNumber}</span>}
                {x.serialNumber && <span className="opacity-60"> · {x.serialNumber}</span>}
              </Chip>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══ Billing ═══ */
function SiteBilling({ site, compact = false }) {
  const bills = site.bills || [];
  if (!bills.length) return <span className="text-ink-300 text-xs">{compact ? "No bills" : "No bills yet"}</span>;

  if (compact) {
    return (
      <div className="space-y-1.5">
        {bills.slice(0, 2).map(b => (
          <div key={b._id} className="text-xs leading-tight">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-ink-400">{formatDate(b.billDate)}</span>
              <span className={cx("px-1.5 py-0.5 rounded-full font-medium capitalize", BILL_TONE[b.status] || BILL_TONE.draft)}>{b.status}</span>
            </div>
            <div className="text-ink-700 font-semibold mt-0.5 tnum">
              {formatINR(b.paidAmount)} <span className="text-ink-400 font-normal">/ {formatINR(b.grandTotal)}</span>
            </div>
          </div>
        ))}
        {bills.length > 2 && <span className="text-xs text-brand-600 font-medium">+{bills.length - 2} more</span>}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {bills.map(b => (
        <Link key={b._id} to={`/admin/bills/${b._id}`}
          className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-white border border-ink-100 hover:border-brand-200 hover:bg-brand-50/40 transition-colors">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="font-mono text-xs text-ink-700 truncate">{b.billNumber}</span>
            <span className="text-xs text-ink-400 whitespace-nowrap">{formatDate(b.billDate)}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-semibold text-ink-700 whitespace-nowrap tnum">
              {formatINR(b.paidAmount)} <span className="text-ink-400 font-normal">/ {formatINR(b.grandTotal)}</span>
            </span>
            <span className={cx("text-xs font-medium px-2 py-0.5 rounded-full capitalize", BILL_TONE[b.status] || BILL_TONE.draft)}>{b.status}</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

/* ═══ GPS pin indicator ═══ */
function SiteLocationBadge({ site }) {
  const lat = site.coordinates?.lat;
  const lng = site.coordinates?.lng;
  if (lat == null || lng == null) return <span className="text-[10px] text-ink-300">No GPS pin</span>;
  const role = site.locationAddedByRole === "crew" ? "crew" : "admin";
  const when = site.locationAddedAt ? formatDate(site.locationAddedAt) : null;
  return (
    <span title={`${lat.toFixed(5)}, ${lng.toFixed(5)} · set by ${role}${when ? ` on ${when}` : ""}`}
      className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
      <Navigation size={9} /> GPS set
    </span>
  );
}

/* ═══ Schedule ═══
   Where the site sits in its own timeline, plus the two dates that define it.
   `compact` is the table/card form; the full form is used in the detail panel. */
function SiteSchedule({ site, compact = false }) {
  const t = siteTimeline(site);

  if (compact) {
    return (
      <div className="min-w-0">
        <Badge variant={t.tone} size="xs" dot className="max-w-full">{t.label}</Badge>
        <div className="text-[11px] text-ink-400 mt-1 truncate" title={`${formatDate(site.startDate)} → ${formatDate(site.endDate)}`}>
          {site.startDate ? formatDate(site.startDate, { day: "numeric", month: "short" }) : "—"}
          {" → "}
          {site.endDate ? formatDate(site.endDate, { day: "numeric", month: "short", year: "2-digit" }) : "open"}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <Field label="Start Date" value={site.startDate ? dateWithRelative(site.startDate) : null} />
      <Field label="End Date" value={site.endDate ? dateWithRelative(site.endDate) : null} />
    </div>
  );
}

/* ═══ Last activity ═══
   The newest attendance, approved expense or drawing on the site. Comes from
   the `activity` rollup the API adds — it is not on the Site document. */
function SiteActivity({ site, compact = false }) {
  const a = site.activity;
  const last = a?.lastActivityDate;

  if (compact) {
    if (!last) return <span className="text-[11px] text-ink-300">No activity yet</span>;
    return (
      <span className={cx("text-[11px] font-medium", {
        success: "text-emerald-600", warning: "text-amber-600",
        danger: "text-red-600", default: "text-ink-400",
      }[activityTone(last)])} title={formatDate(last)}>
        Active {relativeDate(last)}
      </span>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <Field label="Last Activity" value={last ? dateWithRelative(last) : null} />
      <Field label="Attendance Logged" value={a?.attendanceDays ? `${a.attendanceDays} day${a.attendanceDays > 1 ? "s" : ""}` : null} />
      <Field label="Last Attendance" value={a?.lastAttendanceDate ? dateWithRelative(a.lastAttendanceDate) : null} />
      <Field label="Last Expense" value={a?.lastExpenseDate ? dateWithRelative(a.lastExpenseDate) : null} />
      <Field label="Approved Expenses" value={a?.expenseCount ? `${formatINR(a.expenseTotal)} · ${a.expenseCount} record${a.expenseCount > 1 ? "s" : ""}` : null} />
      <Field label="Last Drawing" value={a?.lastDrawingAt ? dateWithRelative(a.lastDrawingAt) : null} />
    </div>
  );
}

/* ═══ Record dates ═══ every remaining timestamp the site carries */
function SiteRecordDates({ site }) {
  const pinnedBy = site.locationAddedByRole === "crew" ? "crew" : "admin";
  return (
    <div className="grid grid-cols-2 gap-4">
      <Field label="Created" value={dateWithRelative(site.createdAt)} />
      <Field label="Last Updated" value={dateWithRelative(site.updatedAt)} />
      <Field
        label="GPS Pinned"
        value={site.locationAddedAt ? `${dateWithRelative(site.locationAddedAt)} · by ${pinnedBy}` : null}
        className="col-span-2"
      />
    </div>
  );
}

/* ═══ View modal ═══ */
function ViewModal({ site, onClose, onEdit, onAssign, onLocation }) {
  if (!site) return null;
  const hasGPS = site.coordinates?.lat != null && site.coordinates?.lng != null;

  return (
    <Modal open onClose={onClose} title={site.name} subtitle={site.code ? `#${site.code}` : undefined}
      icon={<MapPin size={18} />} size="lg">
      <div className="space-y-6">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={site.status} />
          {(() => { const t = siteTimeline(site); return <Badge variant={t.tone} dot>{t.label}</Badge>; })()}
          {site.clientId?.name && (
            <Badge variant="brand">{site.clientId.name}{site.clientId.company ? ` — ${site.clientId.company}` : ""}</Badge>
          )}
        </div>

        <div className="bg-ink-50 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Address" value={[site.address, site.city, site.state, site.country].filter(Boolean).join(", ")} className="sm:col-span-2" />
          {site.description && <Field label="Notes" value={site.description} className="sm:col-span-2" />}
        </div>

        <section>
          <h4 className="text-sm font-semibold text-ink-800 mb-3">Schedule</h4>
          <div className="bg-ink-50 rounded-2xl p-4"><SiteSchedule site={site} /></div>
        </section>

        <section>
          <h4 className="text-sm font-semibold text-ink-800 mb-3">Activity</h4>
          <div className="bg-ink-50 rounded-2xl p-4"><SiteActivity site={site} /></div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3 gap-3">
            <h4 className="text-sm font-semibold text-ink-800">Location</h4>
            <button onClick={() => { onClose(); onLocation(site); }}
              className="text-xs font-semibold text-brand-600 hover:text-brand-700 shrink-0">
              {hasGPS ? "Update pin →" : "Mark location →"}
            </button>
          </div>
          <div className="bg-ink-50 rounded-2xl p-4 flex items-center gap-3 flex-wrap">
            <SiteLocationBadge site={site} />
            {hasGPS && (
              <a href={`https://www.google.com/maps?q=${site.coordinates.lat},${site.coordinates.lng}`}
                target="_blank" rel="noreferrer"
                className="text-xs font-semibold text-brand-600 hover:text-brand-700 inline-flex items-center gap-1">
                Open in Google Maps <ExternalLink size={11} />
              </a>
            )}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3 gap-3">
            <h4 className="text-sm font-semibold text-ink-800">Assigned Resources</h4>
            <button onClick={() => { onClose(); onAssign(site); }}
              className="text-xs font-semibold text-brand-600 hover:text-brand-700 shrink-0">Manage →</button>
          </div>
          <div className="bg-ink-50 rounded-2xl p-4"><AssignedResources site={site} /></div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3 gap-3">
            <h4 className="text-sm font-semibold text-ink-800">Bills</h4>
            <Link to={`/admin/bills?siteId=${site._id}`} className="text-xs font-semibold text-brand-600 hover:text-brand-700 shrink-0">View all →</Link>
          </div>
          <div className="bg-ink-50 rounded-2xl p-3"><SiteBilling site={site} /></div>
        </section>

        <section>
          <h4 className="text-sm font-semibold text-ink-800 mb-3">Record</h4>
          <div className="bg-ink-50 rounded-2xl p-4"><SiteRecordDates site={site} /></div>
        </section>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3 pt-1 border-t border-ink-100">
          <Link to={`/admin/sites/${site._id}`} className="text-sm font-semibold text-brand-600 hover:text-brand-700 text-center sm:text-left">
            Open full detail page →
          </Link>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} block className="sm:w-auto">Close</Button>
            <Button onClick={() => { onClose(); onEdit(site); }} block className="sm:w-auto">Edit Site</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ═══ Mark location ═══
   Short Google Maps links (maps.app.goo.gl) carry no coordinates — they're an
   id that has to be redirect-followed server side, which the browser can't do
   because of CORS. Plain coordinates parse instantly with no backend at all. */
function MarkLocationModal({ site, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [coords, setCoords] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const validate = (lat, lng) => {
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
  };

  const parseDirect = (input) => {
    const s = input.trim();
    let m = s.match(/@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);        // .../@10.62,76.19,17z/...
    if (m) return validate(+m[1], +m[2]);
    m = s.match(/[?&]q=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);        // ...?q=10.62,76.19
    if (m) return validate(+m[1], +m[2]);
    m = s.match(/!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/);
    if (m) return validate(+m[1], +m[2]);
    m = s.match(/(-?\d{1,3}\.\d+)[,\s]+(-?\d{1,3}\.\d+)/);         // plain "lat, lng"
    if (m) return validate(+m[1], +m[2]);
    return null;
  };

  const isShortLink = (s) => /maps\.app\.goo\.gl|goo\.gl\/maps/i.test(s);

  const resolve = async () => {
    setError("");
    const raw = text.trim();
    if (!raw) return;
    const direct = parseDirect(raw);
    if (direct) { setCoords(direct); return; }
    if (isShortLink(raw)) {
      setResolving(true);
      try {
        const { data } = await api.post("/resolve-maps-link", { url: raw });
        const c = validate(data.data?.lat, data.data?.lng);
        if (c) setCoords(c);
        else setError("Couldn't read coordinates from that link.");
      } catch {
        setError("Couldn't resolve that link. Paste the coordinates instead (long-press in Google Maps → copy the numbers).");
      } finally { setResolving(false); }
      return;
    }
    setError("Couldn't read coordinates. Paste them like: 10.627064, 76.197189");
  };

  const handleChange = (v) => {
    setText(v);
    setError("");
    setCoords(parseDirect(v) || null);
  };

  const handleConfirm = async () => {
    if (!coords) return;
    setSaving(true);
    try {
      await api.post(`/sites/locations/${site._id}/mark-location-admin`, {
        lat: coords.lat, lng: coords.lng, accuracy: 0, source: "manual-map",
      });
      toast.success("Location saved!");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save location");
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="Mark Site Location" subtitle={site.name} icon={<Navigation size={18} />} size="lg">
      <div className="space-y-4">
        <div className="bg-brand-50 border border-brand-100 rounded-2xl px-4 py-3 text-xs text-brand-800 leading-relaxed">
          Open <b>Google Maps</b>, long-press the exact spot, then copy either the <b>coordinates</b> or the <b>share link</b> and paste it below.
        </div>

        <Input
          label="Coordinates or Google Maps link"
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="10.627064, 76.197189  or  https://maps.app.goo.gl/…"
          className="font-mono"
        />

        {isShortLink(text) && !coords && (
          <Button size="sm" onClick={resolve} loading={resolving}>Get coordinates from link</Button>
        )}

        {error && <p className="text-xs text-red-600">{error}</p>}

        {coords && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-2xl px-3 py-2.5 flex-wrap">
            <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
            <span className="text-xs font-mono text-emerald-800">{coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}</span>
            <a href={`https://www.google.com/maps?q=${coords.lat},${coords.lng}`} target="_blank" rel="noreferrer"
              className="text-xs font-semibold text-brand-600 hover:text-brand-700 ml-auto inline-flex items-center gap-1 whitespace-nowrap">
              Verify on map <ExternalLink size={11} />
            </a>
          </div>
        )}

        <StickyActions>
          <Button variant="secondary" onClick={onClose} block className="sm:w-auto">Cancel</Button>
          <Button onClick={handleConfirm} disabled={!coords} loading={saving} block className="sm:w-auto">Confirm & Save</Button>
        </StickyActions>
      </div>
    </Modal>
  );
}

/* ═══ Mobile card ═══ */
function SiteCard({ site, onView, onAssign, onEdit, onDelete, onLocation }) {
  return (
    <Card className="p-4 space-y-3" hover>
      {/* actions lead the card, mirroring the table's first column */}
      <div className="flex items-center gap-1.5 pb-3 border-b border-ink-100">
        <Button size="xs" variant="outline" icon={<Eye size={13} />} onClick={() => onView(site)}>View</Button>
        <Button size="xs" variant="outline" icon={<Users size={13} />} onClick={() => onAssign(site)}>Assign</Button>
        <IconButton size="sm" variant="outline" icon={<Navigation size={14} />} onClick={() => onLocation(site)} title="Mark location" />
        <div className="ml-auto flex gap-0.5">
          <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => onEdit(site)} title="Edit" />
          <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => onDelete(site._id)} title="Delete" />
        </div>
      </div>

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className={cx("w-11 h-11 rounded-xl border-2 grid place-items-center font-bold shrink-0", STATUS_RING[site.status] || "bg-ink-50 border-ink-200 text-ink-500")}>
            {site.name?.[0]?.toUpperCase()}
          </span>
          <div className="min-w-0">
            <Link to={`/admin/sites/${site._id}`} className="font-semibold text-ink-900 hover:text-brand-600 transition-colors block truncate">
              {site.name}
            </Link>
            <div className="text-xs text-ink-400 truncate">
              {[site.city, site.state].filter(Boolean).join(", ") || "No location set"}
            </div>
          </div>
        </div>
        <StatusBadge status={site.status} />
      </div>

      <div className="flex flex-wrap gap-1.5 items-center">
        {site.code && <span className="font-mono text-[11px] bg-ink-100 border border-ink-200 px-2 py-0.5 rounded-md text-ink-600">{site.code}</span>}
        {site.clientId?.name && <Chip tone="brand">{site.clientId.name}</Chip>}
        <SiteLocationBadge site={site} />
      </div>

      <div className="flex items-center justify-between gap-3 rounded-xl bg-ink-50 px-3 py-2">
        <SiteSchedule site={site} compact />
        <SiteActivity site={site} compact />
      </div>

      <div className="grid grid-cols-2 gap-3 pt-1">
        <div>
          <p className="text-[10px] font-semibold text-ink-400 uppercase tracking-wider mb-1.5">Resources</p>
          <AssignedResources site={site} compact />
        </div>
        <div>
          <p className="text-[10px] font-semibold text-ink-400 uppercase tracking-wider mb-1.5">Billing</p>
          <SiteBilling site={site} compact />
        </div>
      </div>
    </Card>
  );
}

/* ═══ Sortable header cell ═══ */
function SortHeader({ label, field, sort, onSort, className = "" }) {
  if (!field) return <th className={cx(thCls, className)}>{label}</th>;
  const active = sort.by === field;
  const Arrow = !active ? ArrowUpDown : sort.order === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={cx(thCls, active ? "text-brand-700" : "text-ink-500", className)}>
      <button type="button" onClick={() => onSort(field)} title={`Sort by ${label.toLowerCase()}`}
        className="inline-flex items-center gap-1.5 max-w-full hover:text-brand-700 transition-colors group">
        <span className="truncate">{label}</span>
        <Arrow size={12} className={cx("shrink-0 transition-opacity", active ? "opacity-100" : "opacity-30 group-hover:opacity-70")} />
      </button>
    </th>
  );
}

/* ═══ Advanced panel controls ═══ */
const panelSelectCls = "w-full appearance-none bg-white border border-ink-300 rounded-xl pl-3 pr-8 py-2.5 text-sm text-ink-700 cursor-pointer hover:border-ink-400 focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all";
const panelLabelCls = "text-[11px] font-semibold text-ink-400 uppercase tracking-wider";

function TriSelect({ label, value, onChange, yes = "Yes", no = "No" }) {
  return (
    <div className="flex flex-col gap-1">
      <label className={panelLabelCls}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={panelSelectCls}>
        <option value="">Any</option>
        <option value="true">{yes}</option>
        <option value="false">{no}</option>
      </select>
    </div>
  );
}

function PanelSelect({ label, value, onChange, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className={panelLabelCls}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={panelSelectCls}>{children}</select>
    </div>
  );
}

function PanelDate({ label, value, onChange }) {
  return (
    <div className="flex flex-col gap-1">
      <label className={panelLabelCls}>{label}</label>
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-ink-300 rounded-xl px-3 py-2.5 text-sm text-ink-700 hover:border-ink-400 focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all" />
    </div>
  );
}

export default function AdminSites() {
  const [sites, setSites] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(INIT_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [sort, setSort] = useState({ by: "createdAt", order: "desc" });
  const [showFilters, setShowFilters] = useState(false);
  const [options, setOptions] = useState({ cities: [], states: [], countries: [], statusCounts: {} });
  const [loading, setLoading] = useState(true);
  const [crews, setCrews] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [locationSite, setLocationSite] = useState(null);
  const [form, setForm] = useState(INIT);
  const [saving, setSaving] = useState(false);
  const [assignTab, setAssignTab] = useState("crews");
  const [assignSel, setAssignSel] = useState({ crews: [], vehicles: [], instruments: [] });
  const debRef = useRef(null);

  const fetchSites = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: LIMIT, sortBy: sort.by, sortOrder: sort.order });
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) p.set(k, v); });
      const { data } = await api.get(`/sites/admin?${p}`);
      setSites(data.data.sites); setTotal(data.data.total); setPages(data.data.pages);
    } catch { toast.error("Failed to load sites"); }
    finally { setLoading(false); }
  }, [page, filters, sort]);

  useEffect(() => {
    Promise.all([api.get("/crews?limit=200"), api.get("/vehicles?limit=200"), api.get("/instruments?limit=200")])
      .then(([cr, v, i]) => {
        setCrews(cr.data.data.crews || cr.data.data || []);
        setVehicles(v.data.data.vehicles || v.data.data || []);
        setInstruments(i.data.data.instruments || i.data.data || []);
      }).catch(() => { });
    api.get("/sites/admin/filter-options")
      .then(({ data }) => setOptions(o => ({ ...o, ...(data.data || {}) })))
      .catch(() => { });
  }, []);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  /* filters / sorting ------------------------------------------------------ */
  const setFilter = (key, value) => { setFilters(prev => ({ ...prev, [key]: value })); setPage(1); };

  const handleSearch = (v) => {
    setSearchInput(v);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => setFilter("search", v), 300);
  };

  const handleSort = (field) => {
    setSort(prev => prev.by === field
      ? { by: field, order: prev.order === "asc" ? "desc" : "asc" }
      : { by: field, order: DEFAULT_ORDER(field) });
    setPage(1);
  };

  const clearAll = () => {
    clearTimeout(debRef.current);
    setSearchInput("");
    setFilters(INIT_FILTERS);
    setSort({ by: "createdAt", order: "desc" });
    setPage(1);
  };

  // "search" lives in its own box, so it isn't counted as an advanced filter
  const activeCount = Object.entries(filters).filter(([k, v]) => k !== "search" && v !== "").length;
  const anyFilter = activeCount > 0 || !!filters.search;
  const sortValue = `${sort.by}:${sort.order}`;

  /* CRUD ------------------------------------------------------------------- */
  const openCreate = () => { setForm(INIT); setSelected(null); setModal("create"); };
  const openEdit = (s) => {
    setSelected(s);
    setForm({
      name: s.name || "", code: s.code || "", address: s.address || "", city: s.city || "", state: s.state || "",
      country: s.country || "India", status: s.status || "active", clientId: s.clientId?._id || "",
      description: s.description || "",
      startDate: s.startDate ? s.startDate.split("T")[0] : "",
      endDate: s.endDate ? s.endDate.split("T")[0] : "",
    });
    setModal("edit");
  };
  const openAssign = (s) => {
    setSelected(s);
    setAssignSel({
      crews: (s.assignedCrews || []).map(x => x._id || x),
      vehicles: (s.assignedVehicles || []).map(x => x._id || x),
      instruments: (s.assignedInstruments || []).map(x => x._id || x),
    });
    setModal("assign");
  };
  const openView = (s) => { setSelected(s); setModal("view"); };
  const openLocation = (s) => { setLocationSite(s); setModal("location"); };
  const closeLocation = () => { setModal(null); setLocationSite(null); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal === "create") { await api.post("/sites/admin", form); toast.success("Site created!"); }
      else { await api.put(`/sites/admin/${selected._id}`, form); toast.success("Site updated!"); }
      setModal(null); fetchSites();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this site? This cannot be undone.")) return;
    try { await api.delete(`/sites/admin/${id}`); toast.success("Deleted"); fetchSites(); }
    catch { toast.error("Failed"); }
  };

  const handleAssign = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.put(`/sites/admin/${selected._id}/assign-crews`, { crews: assignSel.crews }),
        api.put(`/sites/admin/${selected._id}/assign-vehicles`, { vehicles: assignSel.vehicles }),
        api.put(`/sites/admin/${selected._id}/assign-instruments`, { instruments: assignSel.instruments }),
      ]);
      toast.success("Assignments saved!"); setModal(null); fetchSites();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const toggle = (type, id) => setAssignSel(prev => ({
    ...prev,
    [type]: prev[type].includes(id) ? prev[type].filter(x => x !== id) : [...prev[type], id],
  }));

  const f = k => ({ value: form[k], onChange: e => setForm({ ...form, [k]: e.target.value }) });

  const ASSIGN_TABS = [
    { key: "crews", label: "Crew", icon: <Users size={14} /> },
    { key: "vehicles", label: "Vehicles", icon: <Truck size={14} /> },
    { key: "instruments", label: "Instruments", icon: <Telescope size={14} /> },
  ];

  return (
    <AdminLayout fullHeight>
      <ListPage>
        <PageHeader
          title="Sites"
          subtitle={`${total} project site${total !== 1 ? "s" : ""}`}
          action={<Button onClick={openCreate} icon={<Plus size={16} />} className="w-full sm:w-auto">New Site</Button>}
        />

        {/* ══ Filters ══ */}
        <FilterBar className="shrink-0">
          <SearchBar value={searchInput} onChange={handleSearch} placeholder="Search name, code, city…" className="flex-1 min-w-[170px]" />
          <FilterSelect value={filters.status} onChange={v => setFilter("status", v)} ariaLabel="Filter by status" className="min-w-[130px]">
            <option value="">All Status</option>
            {STATUSES.map(s => (
              <option key={s} value={s} className="capitalize">
                {s}{options.statusCounts?.[s] ? ` (${options.statusCounts[s]})` : ""}
              </option>
            ))}
          </FilterSelect>
          <div className="min-w-[190px]">
            <ClientSelectPopup placeholder="All Clients" value={filters.clientId} onChange={(id) => setFilter("clientId", id)} />
          </div>
          <FilterSelect
            value={sortValue}
            onChange={v => { const [by, order] = v.split(":"); setSort({ by, order }); setPage(1); }}
            ariaLabel="Sort sites"
            className="min-w-[165px]"
          >
            {/* a header click can produce a combo that isn't in the preset list */}
            {!SORT_OPTIONS.some(o => o.value === sortValue) && (
              <option value={sortValue}>Sort: {sort.by} ({sort.order})</option>
            )}
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>Sort: {o.label}</option>)}
          </FilterSelect>
          <button
            onClick={() => setShowFilters(s => !s)}
            aria-expanded={showFilters}
            className={cx("inline-flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap px-3 py-2.5 rounded-xl border transition-all",
              activeCount ? "bg-brand-50 border-brand-300 text-brand-700" : "bg-white border-ink-300 text-ink-600 hover:border-ink-400 hover:bg-ink-50")}
          >
            <SlidersHorizontal size={14} />
            {showFilters ? "Hide filters" : "More filters"}
            {activeCount > 0 && <span className="bg-brand-600 text-white rounded-full px-1.5 text-[10px] tnum">{activeCount}</span>}
          </button>
          {anyFilter && (
            <button onClick={clearAll} className="text-xs font-semibold text-brand-600 hover:text-brand-700 px-1 whitespace-nowrap">Clear all</button>
          )}
        </FilterBar>

        {/* ══ Advanced filters ══ */}
        {showFilters && (
          <Card className="shrink-0 max-h-[42vh] overflow-y-auto p-4 space-y-4 animate-fade-up">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <PanelSelect label="City" value={filters.city} onChange={v => setFilter("city", v)}>
                <option value="">All cities</option>
                {options.cities.map(c => <option key={c} value={c}>{c}</option>)}
              </PanelSelect>
              <PanelSelect label="State" value={filters.state} onChange={v => setFilter("state", v)}>
                <option value="">All states</option>
                {options.states.map(s => <option key={s} value={s}>{s}</option>)}
              </PanelSelect>
              <PanelSelect label="Country" value={filters.country} onChange={v => setFilter("country", v)}>
                <option value="">All countries</option>
                {options.countries.map(c => <option key={c} value={c}>{c}</option>)}
              </PanelSelect>
              <PanelSelect label="Crew assigned" value={filters.crewId} onChange={v => setFilter("crewId", v)}>
                <option value="">Any crew member</option>
                {crews.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </PanelSelect>
              <PanelSelect label="Vehicle assigned" value={filters.vehicleId} onChange={v => setFilter("vehicleId", v)}>
                <option value="">Any vehicle</option>
                {vehicles.map(v => <option key={v._id} value={v._id}>{v.name}{v.registrationNumber ? ` · ${v.registrationNumber}` : ""}</option>)}
              </PanelSelect>
              <PanelSelect label="Instrument assigned" value={filters.instrumentId} onChange={v => setFilter("instrumentId", v)}>
                <option value="">Any instrument</option>
                {instruments.map(i => <option key={i._id} value={i._id}>{i.name}{i.serialNumber ? ` · ${i.serialNumber}` : ""}</option>)}
              </PanelSelect>
              <TriSelect label="GPS pin" value={filters.hasLocation} onChange={v => setFilter("hasLocation", v)} yes="Marked" no="Not marked" />
              <TriSelect label="Client linked" value={filters.hasClient} onChange={v => setFilter("hasClient", v)} yes="Has client" no="No client" />
              <TriSelect label="Crew" value={filters.hasCrews} onChange={v => setFilter("hasCrews", v)} yes="Has crew" no="None" />
              <TriSelect label="Vehicles" value={filters.hasVehicles} onChange={v => setFilter("hasVehicles", v)} yes="Has vehicles" no="None" />
              <TriSelect label="Instruments" value={filters.hasInstruments} onChange={v => setFilter("hasInstruments", v)} yes="Has instruments" no="None" />
              <TriSelect label="Bills" value={filters.hasBills} onChange={v => setFilter("hasBills", v)} yes="Has bills" no="Not billed" />
            </div>

            <div className="pt-3 border-t border-ink-100 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <PanelDate label="Started from" value={filters.startFrom} onChange={v => setFilter("startFrom", v)} />
              <PanelDate label="Started to" value={filters.startTo} onChange={v => setFilter("startTo", v)} />
              <PanelDate label="Ends from" value={filters.endFrom} onChange={v => setFilter("endFrom", v)} />
              <PanelDate label="Ends to" value={filters.endTo} onChange={v => setFilter("endTo", v)} />
              <PanelDate label="Running on" value={filters.activeOn} onChange={v => setFilter("activeOn", v)} />
            </div>

            {activeCount > 0 && (
              <div className="flex justify-end">
                <button onClick={() => { setFilters(prev => ({ ...INIT_FILTERS, search: prev.search })); setPage(1); }}
                  className="text-xs font-semibold text-brand-600 hover:text-brand-700">Reset these filters</button>
              </div>
            )}
          </Card>
        )}

        {/* ══ Results ══ */}
        {loading ? (
          <div className="flex-1 min-h-0 overflow-hidden"><ListSkeleton rows={8} cols={8} from="xl" /></div>
        ) : sites.length === 0 ? (
          <Card className="flex-1 min-h-0 grid place-items-center">
            <EmptyState icon={<MapPin size={22} />} title="No sites found"
              description={anyFilter ? "Try adjusting your search or filters." : "Add your first project site."}
              action={anyFilter
                ? <Button variant="secondary" onClick={clearAll}>Clear filters</Button>
                : <Button onClick={openCreate} icon={<Plus size={16} />}>Create Site</Button>} />
          </Card>
        ) : (
          <ListSurface from="xl" footer={<Pagination page={page} pages={pages} total={total} limit={LIMIT} onPage={setPage} />}>
            {/* ── Desktop table ── */}
            <table className={listTableWideCls}>
              <thead className={stickyHeadCls}>
                <tr>
                  <th className={cx(thCls, actionHeadCls, actionW(5))}>Actions</th>
                  {/* the code rides along with the name, which buys the row a column */}
                  <SortHeader label="Site" field="name" sort={sort} onSort={handleSort} />
                  <SortHeader label="Location" field="city" sort={sort} onSort={handleSort} />
                  <SortHeader label="Client" field={null} sort={sort} onSort={handleSort} />
                  <SortHeader label="Schedule" field="startDate" sort={sort} onSort={handleSort} className="w-[150px]" />
                  <SortHeader label="Resources" field="crews" sort={sort} onSort={handleSort} className="w-[150px]" />
                  <SortHeader label="Billing" field="bills" sort={sort} onSort={handleSort} className="w-[140px]" />
                  <SortHeader label="Status" field="status" sort={sort} onSort={handleSort} className="w-[112px]" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {sites.map(site => (
                  <tr key={site._id} className="group hover:bg-ink-50 transition-colors">
                    <td className={cx(tdCls, actionColCls, "px-3")}>
                      <div className="flex gap-0.5">
                        <IconButton size="sm" variant="outline" icon={<Eye size={15} />} onClick={() => openView(site)} title="Quick view" />
                        <IconButton size="sm" variant="outline" icon={<Users size={15} />} onClick={() => openAssign(site)} title="Assign resources" />
                        <IconButton size="sm" variant="outline" icon={<Navigation size={15} />} onClick={() => openLocation(site)} title="Mark location" />
                        <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => openEdit(site)} title="Edit" />
                        <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(site._id)} title="Delete" />
                      </div>
                    </td>
                    <td className={tdCls}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={cx("w-9 h-9 rounded-xl border-2 grid place-items-center font-bold text-sm shrink-0", STATUS_RING[site.status] || "bg-ink-50 border-ink-200 text-ink-500")}>
                          {site.name?.[0]?.toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <Link to={`/admin/sites/${site._id}`} className="block font-semibold text-ink-900 hover:text-brand-600 transition-colors truncate">
                            {site.name}
                          </Link>
                          <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                            {site.code && <span className="font-mono text-[11px] bg-ink-100 px-1.5 rounded text-ink-600 shrink-0">{site.code}</span>}
                            <SiteActivity site={site} compact />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={cx(tdCls, "text-ink-500 text-xs")}>
                      <div className="truncate">{[site.city, site.state].filter(Boolean).join(", ") || <span className="text-ink-300">—</span>}</div>
                      <div className="mt-1"><SiteLocationBadge site={site} /></div>
                    </td>
                    <td className={tdCls}>
                      {site.clientId?.name ? <Chip tone="brand">{site.clientId.name}</Chip> : <span className="text-ink-300 text-xs">—</span>}
                    </td>
                    <td className={tdCls}><SiteSchedule site={site} compact /></td>
                    <td className={tdCls}><AssignedResources site={site} compact /></td>
                    <td className={tdCls}><SiteBilling site={site} compact /></td>
                    <td className={tdCls}><StatusBadge status={site.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Mobile / tablet cards ── */}
            <div className={listCardsWideCls}>
              {sites.map(site => (
                <SiteCard key={site._id} site={site} onView={openView} onAssign={openAssign}
                  onEdit={openEdit} onDelete={handleDelete} onLocation={openLocation} />
              ))}
            </div>
          </ListSurface>
        )}
      </ListPage>

      {modal === "view" && (
        <ViewModal site={selected} onClose={() => setModal(null)} onEdit={openEdit} onAssign={openAssign} onLocation={openLocation} />
      )}

      {modal === "location" && locationSite && (
        <MarkLocationModal site={locationSite} onClose={closeLocation} onSaved={() => { closeLocation(); fetchSites(); }} />
      )}

      {/* ══ Create / edit ══ */}
      <Modal
        open={modal === "create" || modal === "edit"}
        onClose={() => setModal(null)}
        title={modal === "create" ? "Create New Site" : "Edit Site"}
        subtitle={modal === "edit" ? selected?.name : "New project site"}
        icon={<MapPin size={18} />}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Site Name" required {...f("name")} placeholder="e.g. Highway Project Phase 1" />
            <Input label="Site Code" {...f("code")} placeholder="e.g. HWY-001" className="font-mono" />
          </div>
          <Input label="Address" {...f("address")} placeholder="Full street address" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Input label="City" {...f("city")} />
            <Input label="State" {...f("state")} />
            <Input label="Country" {...f("country")} wrapClass="col-span-2 sm:col-span-1" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select label="Status" {...f("status")}>
              {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
            </Select>
            <ClientSelectPopup label="Client" value={form.clientId} onChange={(id) => setForm({ ...form, clientId: id })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Start Date" type="date" {...f("startDate")} />
            <Input label="End Date" type="date" {...f("endDate")} />
          </div>
          <Textarea label="Description / Notes" {...f("description")} placeholder="Site details, scope, remarks…" />

          <StickyActions>
            <Button type="button" variant="secondary" onClick={() => setModal(null)} block className="sm:w-auto">Cancel</Button>
            <Button type="submit" loading={saving} block className="sm:w-auto">
              {modal === "create" ? "Create Site" : "Save Changes"}
            </Button>
          </StickyActions>
        </form>
      </Modal>

      {/* ══ Assign resources ══ */}
      <Modal open={modal === "assign"} onClose={() => setModal(null)} title="Assign Resources"
        subtitle={selected?.name} icon={<Users size={18} />} size="lg">
        <div className="space-y-4">
          <div className="flex bg-ink-100 rounded-xl p-1 gap-1">
            {ASSIGN_TABS.map(t => (
              <button key={t.key} onClick={() => setAssignTab(t.key)}
                className={cx("flex-1 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5",
                  assignTab === t.key ? "bg-white text-ink-900 shadow-xs" : "text-ink-500 hover:text-ink-700")}>
                {t.icon}
                <span className="hidden xs:inline">{t.label}</span>
                {assignSel[t.key].length > 0 && (
                  <span className="text-[10px] px-1.5 rounded-full bg-brand-100 text-brand-700 tnum">{assignSel[t.key].length}</span>
                )}
              </button>
            ))}
          </div>

          <div className="max-h-[46vh] overflow-y-auto overscroll-contain space-y-1.5 -mx-1 px-1">
            {assignTab === "crews" && (crews.length === 0
              ? <p className="text-ink-400 text-sm text-center py-10">No crew members</p>
              : crews.map(c => (
                <label key={c._id} className={cx("flex items-center gap-3 p-3 rounded-xl cursor-pointer border transition-all",
                  assignSel.crews.includes(c._id) ? "bg-brand-50 border-brand-200" : "bg-white border-ink-200 hover:bg-ink-50")}>
                  <input type="checkbox" checked={assignSel.crews.includes(c._id)} onChange={() => toggle("crews", c._id)}
                    className="w-4 h-4 accent-brand-600 shrink-0" />
                  <span className="w-8 h-8 rounded-full bg-brand-100 grid place-items-center text-brand-700 text-xs font-bold shrink-0">
                    {c.name?.[0]?.toUpperCase()}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-ink-800 truncate">{c.name}</span>
                    <span className="block text-xs text-ink-400">{c.role || "Crew"}</span>
                  </span>
                </label>
              )))}

            {assignTab === "vehicles" && (vehicles.length === 0
              ? <p className="text-ink-400 text-sm text-center py-10">No vehicles</p>
              : vehicles.map(v => (
                <label key={v._id} className={cx("flex items-center gap-3 p-3 rounded-xl cursor-pointer border transition-all",
                  assignSel.vehicles.includes(v._id) ? "bg-brand-50 border-brand-200" : "bg-white border-ink-200 hover:bg-ink-50")}>
                  <input type="checkbox" checked={assignSel.vehicles.includes(v._id)} onChange={() => toggle("vehicles", v._id)}
                    className="w-4 h-4 accent-brand-600 shrink-0" />
                  <Truck size={18} className="text-ink-400 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-ink-800 truncate">{v.name}</span>
                    <span className="block text-xs text-ink-400 truncate">{v.registrationNumber || "—"} · {v.type}</span>
                  </span>
                  <StatusBadge status={v.status} dot={false} />
                </label>
              )))}

            {assignTab === "instruments" && (instruments.length === 0
              ? <p className="text-ink-400 text-sm text-center py-10">No instruments</p>
              : instruments.map(i => (
                <label key={i._id} className={cx("flex items-center gap-3 p-3 rounded-xl cursor-pointer border transition-all",
                  assignSel.instruments.includes(i._id) ? "bg-brand-50 border-brand-200" : "bg-white border-ink-200 hover:bg-ink-50")}>
                  <input type="checkbox" checked={assignSel.instruments.includes(i._id)} onChange={() => toggle("instruments", i._id)}
                    className="w-4 h-4 accent-brand-600 shrink-0" />
                  <Telescope size={18} className="text-ink-400 shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-ink-800 truncate">{i.name}</span>
                    <span className="block text-xs text-ink-400">{i.serialNumber || "—"} · {i.type || "Instrument"}</span>
                  </span>
                </label>
              )))}
          </div>

          <StickyActions>
            <Button variant="secondary" onClick={() => setModal(null)} block className="sm:w-auto">Cancel</Button>
            <Button onClick={handleAssign} loading={saving} block className="sm:w-auto">Save Assignments</Button>
          </StickyActions>
        </div>
      </Modal>
    </AdminLayout>
  );
}
